import React, { useContext, useState } from "react";
import useSound from "use-sound";
import Popover from "@material-ui/core/Popover";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import ButtonBase from "@material-ui/core/ButtonBase";
import IconButton from "@material-ui/core/IconButton";
import Slider from "@material-ui/core/Slider";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import { makeStyles, alpha } from "@material-ui/core/styles";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import WbSunnyOutlinedIcon from "@material-ui/icons/WbSunnyOutlined";
import Brightness3OutlinedIcon from "@material-ui/icons/Brightness3Outlined";
import SettingsBrightnessIcon from "@material-ui/icons/SettingsBrightness";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeDownIcon from "@material-ui/icons/VolumeDown";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import { i18n } from "../../translate/i18n";
import { useSoundPreferences } from "../../utils/soundPreferences";
import { FormDialogHeader, useFormDialogStyles } from "../FormDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import alertSound from "../../assets/sound.mp3";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 288,
    maxWidth: "calc(100vw - 32px)",
    borderRadius: 12,
    padding: theme.spacing(1, 0),
  },
  header: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    padding: theme.spacing(1, 2, 1.5),
  },
  avatar: {
    width: 40,
    height: 40,
    fontSize: "0.95rem",
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  who: { minWidth: 0, flex: 1 },
  nameRow: { display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
  name: {
    fontWeight: 700,
    fontSize: "0.9rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  roleChip: {
    flexShrink: 0,
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "1px 7px",
    borderRadius: 999,
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.type === "dark" ? 0.2 : 0.1),
  },
  line: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  item: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.1, 2),
    fontSize: "0.875rem",
    textAlign: "left",
    "&:hover": { backgroundColor: theme.palette.action.hover },
    "&.Mui-focusVisible": { backgroundColor: theme.palette.action.focus },
    "& svg": { fontSize: 20, color: theme.palette.text.secondary },
  },
  itemHint: {
    marginLeft: "auto",
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  danger: {
    color: theme.palette.error.main,
    "& svg": { color: theme.palette.error.main },
  },
  sectionLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.25, 2, 0.75),
  },
  segmented: {
    display: "flex",
    gap: 2,
    margin: theme.spacing(0, 2, 1),
    padding: 3,
    borderRadius: 9,
    backgroundColor: theme.palette.type === "dark" ? alpha("#000", 0.25) : alpha("#000", 0.05),
  },
  segment: {
    flex: 1,
    gap: 5,
    padding: theme.spacing(0.6, 0),
    borderRadius: 7,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    "& svg": { fontSize: 15 },
    "&.Mui-focusVisible": { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
  },
  segmentActive: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.type === "dark" ? "0 1px 3px rgba(0,0,0,0.45)" : "0 1px 3px rgba(0,0,0,0.12)",
  },
  volumeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 0, 1.25),
  },
  slider: { flex: 1, minWidth: 0 },
  volumeValue: {
    width: 34,
    textAlign: "right",
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  testButton: {
    minWidth: 0,
    padding: "2px 10px",
    fontSize: "0.72rem",
    textTransform: "none",
    borderRadius: 7,
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1, 0, 2),
    fontSize: "0.82rem",
  },
  divider: { margin: theme.spacing(0.75, 0) },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 0.5),
  },
  statusLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.78rem",
    fontWeight: 600,
    "&::before": {
      content: '""',
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
  },
  statusOnline: { color: theme.palette.success.main },
  statusAway: { color: theme.palette.warning.main },
  statusHint: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 2, 0.75),
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
}));

const getInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (first + last).toUpperCase();
};

const THEME_OPTIONS = [
  { value: "light", icon: <WbSunnyOutlinedIcon />, label: "accountMenu.light" },
  { value: "dark", icon: <Brightness3OutlinedIcon />, label: "accountMenu.dark" },
  { value: "system", icon: <SettingsBrightnessIcon />, label: "accountMenu.system" },
];

// Confirmação de saída. Não há opção de "sair dos outros dispositivos": com
// uma sessão por usuário, entrar aqui já encerrou as sessões anteriores.
const LogoutDialog = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="logout-dialog-title"
    >
      <FormDialogHeader
        id="logout-dialog-title"
        title={i18n.t("accountMenu.logoutTitle")}
        onClose={onClose}
      />
      <DialogContent dividers>
        <Typography variant="body2">{i18n.t("accountMenu.logoutText")}</Typography>
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        <Button
          onClick={onClose}
          className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
        >
          {i18n.t("accountMenu.cancel")}
        </Button>
        <Button
          variant="contained"
          disableElevation
          autoFocus
          className={`${dialogClasses.actionButton} ${classes.dangerButton}`}
          onClick={onConfirm}
        >
          {i18n.t("accountMenu.logout")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Menu único da conta: aberto pelo rodapé da barra lateral e pelo avatar do
// topo. Fica no layout (fora do Drawer) para não desmontar no mobile.
const AccountMenu = ({
  anchorEl,
  open,
  onClose,
  anchorOrigin,
  transformOrigin,
  onOpenProfile,
  onOpenSuggestion,
}) => {
  const classes = useStyles();
  const { user, handleLogout, updateUser } = useContext(AuthContext);
  const [savingAway, setSavingAway] = useState(false);
  const { colorMode } = useContext(ColorModeContext);
  const [sound, updateSound] = useSoundPreferences();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const testVolume = sound.muted ? 0 : sound.volume;
  const [playTest] = useSound(alertSound, { volume: testVolume });

  const themePreference = colorMode?.themePreference || "system";
  const isAdmin = user?.profile === "admin";
  const volumePercent = Math.round((sound.muted ? 0 : sound.volume) * 100);

  const handleVolume = (_e, value) => {
    const volume = Array.isArray(value) ? value[0] : value;
    // Mexer no volume tira do mudo (exceto ao levar a zero).
    updateSound({ volume, muted: volume === 0 });
  };

  const toggleMute = () => {
    if (sound.muted || sound.volume === 0) {
      updateSound({ muted: false, volume: sound.volume === 0 ? 0.5 : sound.volume });
    } else {
      updateSound({ muted: true });
    }
  };

  const openLogout = () => {
    onClose();
    setLogoutOpen(true);
  };

  const confirmLogout = () => {
    setLogoutOpen(false);
    handleLogout();
  };

  const muted = sound.muted || sound.volume === 0;
  const away = Boolean(user?.away);

  const toggleAway = async () => {
    const next = !away;
    setSavingAway(true);
    updateUser({ away: next });
    try {
      await api.put("/users/me/presence", { away: next });
    } catch (err) {
      updateUser({ away: !next });
      toastError(err);
    } finally {
      setSavingAway(false);
    }
  };

  return (
    <>
      <Popover
        id="account-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        getContentAnchorEl={null}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.header}>
          <Avatar className={classes.avatar} aria-hidden="true">
            {getInitials(user?.name)}
          </Avatar>
          <div className={classes.who}>
            <div className={classes.nameRow}>
              <span className={classes.name}>{user?.name}</span>
              <span className={classes.roleChip}>
                {i18n.t(isAdmin ? "accountMenu.admin" : "accountMenu.user")}
              </span>
            </div>
            <div className={classes.line}>{user?.email}</div>
            {user?.company?.name && <div className={classes.line}>{user.company.name}</div>}
          </div>
        </div>

        <div className={classes.statusRow}>
          <span className={`${classes.statusLabel} ${away ? classes.statusAway : classes.statusOnline}`}>
            {i18n.t(away ? "accountMenu.away" : "accountMenu.online")}
          </span>
          <Button
            size="small"
            variant="outlined"
            className={classes.testButton}
            disabled={savingAway}
            onClick={toggleAway}
          >
            {i18n.t(away ? "accountMenu.goOnline" : "accountMenu.goAway")}
          </Button>
        </div>
        {away && <div className={classes.statusHint}>{i18n.t("accountMenu.awayHint")}</div>}

        <Divider className={classes.divider} />

        <ButtonBase
          className={classes.item}
          onClick={() => {
            onClose();
            onOpenProfile();
          }}
        >
          <PersonOutlineIcon />
          {i18n.t("accountMenu.profile")}
          <span className={classes.itemHint}>{i18n.t("accountMenu.profileHint")}</span>
        </ButtonBase>

        <Divider className={classes.divider} />

        <div className={classes.sectionLabel} id="account-menu-appearance">
          {i18n.t("accountMenu.appearance")}
        </div>
        <div className={classes.segmented} role="radiogroup" aria-labelledby="account-menu-appearance">
          {THEME_OPTIONS.map((opt) => {
            const active = themePreference === opt.value;
            return (
              <ButtonBase
                key={opt.value}
                role="radio"
                aria-checked={active}
                className={`${classes.segment} ${active ? classes.segmentActive : ""}`}
                onClick={() => colorMode.setThemePreference(opt.value)}
              >
                {opt.icon}
                {i18n.t(opt.label)}
              </ButtonBase>
            );
          })}
        </div>

        <div className={classes.sectionLabel} id="account-menu-sounds">
          {i18n.t("accountMenu.sounds")}
        </div>
        <div className={classes.volumeRow}>
          <Tooltip title={i18n.t(muted ? "accountMenu.unmute" : "accountMenu.mute")}>
            <IconButton size="small" onClick={toggleMute} aria-pressed={muted}>
              {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeDownIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Slider
            className={classes.slider}
            value={volumePercent}
            min={0}
            max={100}
            step={10}
            onChange={(e, v) => handleVolume(e, v / 100)}
            aria-label={i18n.t("accountMenu.volume")}
            getAriaValueText={(v) => `${v}%`}
          />
          <span className={classes.volumeValue}>{volumePercent}%</span>
          <Button
            size="small"
            variant="outlined"
            className={classes.testButton}
            disabled={muted}
            onClick={() => playTest()}
            startIcon={<VolumeUpIcon style={{ fontSize: 14 }} />}
          >
            {i18n.t("accountMenu.test")}
          </Button>
        </div>
        <label className={classes.switchRow} htmlFor="account-sound-tickets">
          {i18n.t("accountMenu.tickets")}
          <Switch
            id="account-sound-tickets"
            size="small"
            color="primary"
            checked={sound.tickets}
            onChange={(e) => updateSound({ tickets: e.target.checked })}
          />
        </label>
        <label className={classes.switchRow} htmlFor="account-sound-chat">
          {i18n.t("accountMenu.chat")}
          <Switch
            id="account-sound-chat"
            size="small"
            color="primary"
            checked={sound.chat}
            onChange={(e) => updateSound({ chat: e.target.checked })}
          />
        </label>

        <Divider className={classes.divider} />

        {onOpenSuggestion && (
          <ButtonBase
            className={classes.item}
            onClick={() => {
              onClose();
              onOpenSuggestion();
            }}
          >
            <EmojiObjectsOutlinedIcon />
            {i18n.t("accountMenu.suggestion")}
          </ButtonBase>
        )}
        <ButtonBase className={`${classes.item} ${classes.danger}`} onClick={openLogout}>
          <ExitToAppIcon />
          {i18n.t("accountMenu.logout")}
        </ButtonBase>
      </Popover>

      <LogoutDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default AccountMenu;
